import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/app/StatusBadge";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { own } from "@/lib/terminology";

export default function Approvals() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/requests/pending-approvals").then(({ data }) => setRows(data.items || [])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const act = async (id, action) => {
    try {
      await api.post(`/requests/${id}/approve`, { action });
      toast.success(action === "approve" ? "Approved" : "Rejected");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-4 max-w-[1400px]">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">{own(user, "Work")}</div>
        <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Approvals Queue</h1>
        <p className="text-sm text-slate-500 mt-0.5">Requests waiting for your review.</p>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded-md">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-slate-500 py-16 text-center border border-dashed border-slate-200 rounded-md bg-white">
          Nothing pending. You&apos;re all caught up.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-md p-4 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm font-semibold text-slate-900">{r.request_code}</span>
                  <StatusBadge value={r.status} />
                  <StatusBadge value={r.priority} />
                </div>
                <div className="text-sm text-slate-600 mt-1 capitalize">
                  {r.request_type?.replace(/_/g, " ")} · {r.asset_ids?.length || 0} asset(s)
                </div>
                <div className="text-xs text-slate-400 mt-0.5">Created {new Date(r.created_at).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => act(r.id, "reject")}>
                  <XCircle className="h-4 w-4 mr-1.5" /> Reject
                </Button>
                <Button size="sm" className="bg-slate-900 hover:bg-slate-800 text-white" onClick={() => act(r.id, "approve")}>
                  <CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
